import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthProvider";
import { OnboardingForm } from "./OnboardingForm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Users, Clapperboard, DollarSign } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState, useRef } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useTranslation } from 'react-i18next';

const FeatureCard = ({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) => {
  const { t } = useTranslation();
  return (
    <div className="feature-card group transition-all duration-300">
      <Icon className="feature-icon group-hover:text-purple-400 transition-colors" />
      <h3 className="text-lg font-semibold mb-2 group-hover:text-purple-400 transition-colors">
        {t(title)}
      </h3>
      <p className="text-sm text-muted-foreground">{t(description)}</p>
    </div>
  );
};

export const Login = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isSignup, setIsSignup] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authAlert, setAuthAlert] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement | null>(null);

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setAuthError(null);
        setEmail("");
        setPassword("");
      }
      if (event === 'USER_UPDATED' || event === 'SIGNED_IN') {
        setAuthError(null);
      }
      if (event === 'SIGNED_IN' && session === null) {
        console.error('Auth error: Session is null');
        setAuthError(t('auth_error_occurred'));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => emailInputRef.current?.focus(), [email]);
  useEffect(() => passwordInputRef.current?.focus(), [password]);
  useEffect(() => confirmPasswordInputRef.current?.focus(), [confirmPassword]);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          redirectTo: `${window.location.origin}/auth/callback`,
        }
      });
      
      if (error) {
        console.error('Google sign in error:', error);
        setAuthError(error.message);
      }
    } catch (error: any) {
      console.error('Google sign in error:', error);
      setAuthError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setAuthError(error.message);
      }
    } catch (error: any) {
      setAuthError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setAuthError(t('passwords_do_not_match'));
      return;
    }
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) {
        if (error.message?.includes('rate limit') || error.status === 429) {
          setAuthError(t('too_many_signups'));
        } else if (error.message?.includes('invalid email')) {
          setAuthError(t('invalid_email'));
        } else {
          setAuthError(error.message);
        }
        return;
      }
      
      if (data.user !== null) {
        setIsSignup(false);
        setAuthError(null);
        setEmail("");
        setPassword("");
        setAuthAlert(t('confirmation_email_sent'));
        setTimeout(() => {
          setAuthAlert("");
        }, 10000);
      }
    } catch (error: any) {
      if (error.message?.includes('rate limit') || error.status === 429) {
        setAuthError(t('too_many_signups'));
      } else if (error.message?.includes('invalid email')) {
        setAuthError(t('invalid_email'));
      } else {
        setAuthError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (user && !profile?.full_name) {
    return (
      <OnboardingForm 
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
        }} 
      />
    );
  }

  if (user) {
    return null;
  }

  const LoginCard = () => (
    <Card className="glass-card border-white/10 shadow-xl backdrop-blur-2xl w-full max-w-md relative overflow-hidden">
      <CardContent className="p-8">
        <div className="absolute -top-32 -right-32 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="text-center mb-8 relative">
          <h2 className="text-2xl font-bold mb-2">{t('welcome_back')}</h2>
          <p className="text-muted-foreground">{t('login_subtitle')}</p>
        </div>

        {authError && (
          <Alert variant="destructive" className="mb-6 bg-red-900/20 backdrop-blur border-red-500/20">
            <AlertDescription>{authError}</AlertDescription>
          </Alert>
        )}

        {authAlert && (
          <Alert variant="default" className="mb-6 bg-blue-900/20 backdrop-blur border-blue-500/20">
            <AlertDescription>{authAlert}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-5">
          <Button 
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-900 border border-white/5 shadow-[0_0_15px_rgba(139,92,246,0.25)] hover:shadow-[0_0_20px_rgba(139,92,246,0.35)] transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('continue_with_google')}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t('or_sign_in_with_email')}</span>
            </div>
          </div>

          <form
            onSubmit={isSignup ? handleEmailSignUp : handleEmailSignIn}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Input 
                type="email" 
                placeholder={t('email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                ref={emailInputRef}
                className="bg-secondary/50 border-white/5 focus:border-purple-500/50"
                required
                id="email-input"
                name="email"
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Input 
                type="password" 
                placeholder={t('password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                ref={passwordInputRef}
                className="bg-secondary/50 border-white/5 focus:border-purple-500/50"
                required
                id="password-input"
                name="password"
                autoComplete="current-password"
              />
            </div>
            {isSignup && (
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder={t('confirm_password')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  ref={confirmPasswordInputRef}
                  className="bg-secondary/50 border-white/5 focus:border-purple-500/50"
                  required
                />
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              {/* <a
                href="#"
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                Forgot password?
              </a> */}
              <a
                onClick={() => setIsSignup(!isSignup)}
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                {isSignup ? t('sign_in') : t('sign_up')}
              </a>
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_20px_rgba(139,92,246,0.5)]"
            >
              {isLoading
                ? isSignup
                  ? t('signing_up')
                  : t('signing_in')
                : isSignup
                ? t('sign_up')
                : t('sign_in')}
            </Button>
          </form>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground opacity-50">{t('copyright')}</p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 overflow-hidden bg-gradient-to-br from-[#15111f] via-[#1A1F2C] to-[#2d1f3d] relative">
      <div className="absolute inset-0 bg-[url('data:image/png;base64,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')] opacity-[0.025]"></div>
      
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-purple-900/10 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-purple-900/10 to-transparent"></div>
      
      <div className="w-full max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-brakle font-bold bg-gradient-to-r from-white via-purple-100 to-purple-200 text-transparent bg-clip-text">
              PREPT.
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-xl">
              {t('marketing_headline')}
            </p>
          </div>

          {isMobile && <LoginCard />}

          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <FeatureCard 
              icon={Clapperboard}
              title={t('production_planning')}
              description={t('production_planning_desc')}
            />
            
            <FeatureCard 
              icon={Camera}
              title={t('equipment_management')}
              description={t('equipment_management_desc')}
            />
            
            <FeatureCard 
              icon={DollarSign}
              title={t('budget_control')}
              description={t('budget_control_desc')}
            />
            
            <FeatureCard 
              icon={Users}
              title={t('team_collaboration')}
              description={t('team_collaboration_desc')}
            />
          </div>
        </div>

        {!isMobile && <LoginCard />}
      </div>
      
      <div className="absolute bottom-2 left-4 text-xs text-white/20">{t('copyright')}</div>
    </div>
  );
};
